/**
 * ResellerProvider — the shell's reseller context (H §7.3), replacing `TenantProvider` at the
 * shell. Mounted by `AppLayout` inside `AuthGate`'s outlet, so a bearer exists. Loads, in
 * parallel:
 *
 *  - tmm-api `GET /v1/reseller-admin/me` — the reseller's name and the member's LIVE role,
 *    which wins over the token's (H §4.4). A failure is a blocking, retryable state.
 *  - saas-auth `GET /api/reseller-admin/brand` — the app-bar logo and name. A failure falls back
 *    silently to the product name from bootstrap.
 *
 * `reloadMe()` re-GETs `/me` after any `RESELLER_ROLE_FORBIDDEN`.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { resellerAdminApi, type ResellerMeView } from '@/api/resellerAdmin';
import { saasAuthResellerApi, type ResellerBrand } from '@/api/saasAuthReseller';
import { useAuth } from '@/auth/authHooks';
import { operatorWithRole, type ResellerRole } from '@/auth/authContextValue';
import { getBootstrap } from '@/config/bootstrap';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ResellerContext, type ResellerContextValue } from './resellerContext';

type LoadState =
  | { kind: 'pending' }
  | { kind: 'failed'; error: unknown }
  | { kind: 'ready'; me: ResellerMeView; brand: ResellerBrand | null };

export function ResellerProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { operator: tokenOperator } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: 'pending' });
  const started = useRef(false);

  const attempt = useCallback(() => {
    setState({ kind: 'pending' });
    const brand = saasAuthResellerApi.brand().catch(() => null);
    Promise.all([resellerAdminApi.me(), brand]).then(
      ([me, loadedBrand]) => setState({ kind: 'ready', me, brand: loadedBrand }),
      (error: unknown) => setState({ kind: 'failed', error }),
    );
  }, []);

  const reloadMe = useCallback(async (): Promise<ResellerRole | null> => {
    try {
      const me = await resellerAdminApi.me();
      setState((prev) => (prev.kind === 'ready' ? { ...prev, me } : prev));
      return me.role;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    attempt();
  }, [attempt]);

  const value = useMemo<ResellerContextValue | null>(() => {
    if (state.kind !== 'ready' || tokenOperator === null) return null;
    const productName = getBootstrap().productName ?? t('app.productName');
    return {
      operator: operatorWithRole(tokenOperator, state.me.role),
      resellerName: state.me.resellerName,
      brand: {
        name: state.brand?.displayName ?? productName,
        logoUrl: state.brand?.logoUrl ?? null,
      },
      reloadMe,
    };
  }, [state, tokenOperator, reloadMe, t]);

  if (state.kind === 'failed') {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        <ApiErrorState
          what={t('errors.what.resellerProfile')}
          error={state.error}
          onRetry={attempt}
        />
      </Box>
    );
  }

  if (value === null) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}
      >
        <CircularProgress aria-label={t('common.loading')} />
      </Box>
    );
  }

  return <ResellerContext.Provider value={value}>{children}</ResellerContext.Provider>;
}
